import React, { useState, useEffect } from 'react'

function QRCodeGeneration() {
    const [qrCodes, setQrCodes] = useState([]);

    // Manual parsing
    const manualParsing = () => {
        const fullPath = window.location.pathname + window.location.search;
        console.log("fullPath", fullPath);

        // Extract id
        const idMatch = fullPath.match(/id=([^&]+)/);
        const extractedId = idMatch ? decodeURIComponent(idMatch[1]) : "SAMPLE123";

        // Fixed grid: 14 rows x 4 columns = 56 total QR codes
        const totalQRCodes = 14 * 4;

        return {
            id: extractedId,
            gridSize: totalQRCodes,
            rows: 14,
            columns: 4
        };
    };

    const { id, gridSize, rows, columns } = manualParsing();

    // Optimized dimensions for A4 printing
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm

    // Margins
    const topMargin = 10; // 15mm from top
    const bottomMargin = 21; // 20mm from bottom
    const leftRightMargin = 5; // 5mm left/right margins

    // Your specified dimensions
    const stickerWidth = 50; // 50mm sticker width
    const stickerHeight = 20; // 20mm sticker height
    const qrCodeSize = 10; // 10mm x 10mm QR code
    const columnGap = 0; // No gap between stickers in a row
    const rowGap = 0; // No gap between stickers in a column

    // Fixed grid dimensions
    const actualRows = 14;
    const actualCols = 4;

    // Generate QR code URL optimized for 10mm x 10mm
    const generateQRCodeURL = (data) => {
        const encodedData = encodeURIComponent(data);
        // Using QR Server API for better quality QR codes
        return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}&format=png&margin=0`;
    };

    // Alternative QR code API
    const generateQRCodeURLAlt = (data) => {
        const encodedData = encodeURIComponent(data);
        // Using chart.googleapis.com as alternative
        return `https://chart.googleapis.com/chart?chs=150x150&cht=qr&chl=${encodedData}&choe=UTF-8`;
    };

    // Generate multiple identical QR codes using only the base ID
    const generateMultipleQRCodes = (baseId, count) => {
        const codes = [];
        for (let i = 0; i < count; i++) {
            codes.push({
                id: i + 1,
                data: baseId,
                url: generateQRCodeURL(baseId),
                altUrl: generateQRCodeURLAlt(baseId)
            });
        }
        return codes;
    };

    useEffect(() => {
        // Always generate QR codes, whether ID is present or not
        const cleanId = id ? id.split('name=')[0] : "SAMPLE123";
        const totalQRCodes = actualRows * actualCols;
        const codes = generateMultipleQRCodes(cleanId, totalQRCodes);
        setQrCodes(codes);
    }, [id, actualRows, actualCols]);

    function extractName(str) {
        if (!str) return "QR Code Grid";
        const match = str.match(/name=([^?]*)/);
        return match ? decodeURIComponent(match[1]) : "QR Code Grid";
    }

    // Extract and decode the product name from the full path
    const getProductName = () => {
        const fullPath = window.location.pathname + window.location.search;
        const nameMatch = fullPath.match(/name=([^&?]*)/);
        if (nameMatch) {
            return decodeURIComponent(nameMatch[1]);
        }
        return "Item name"; // Default name if not found
    };

    const productName = getProductName();

    const handleImageError = (e, qrCode) => {
        // Try alternative URL if primary fails
        if (e.target.src === qrCode.url) {
            e.target.src = qrCode.altUrl;
        } else {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
        }
    };

    return (
        <>
            <div className="print-container" style={{
                width: '210mm',
                height: '297mm',
                maxWidth: '210mm',
                maxHeight: '297mm',
                margin: '0',
                padding: '0',
                fontFamily: 'Arial, sans-serif',
                backgroundColor: 'white',
                boxSizing: 'border-box',
                paddingTop: `${topMargin}mm`,
                paddingBottom: `${bottomMargin}mm`,
                paddingLeft: `${leftRightMargin}mm`,
                paddingRight: `${leftRightMargin}mm`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'hidden'
            }}>
                {/* Custom QR code grid - 14 rows x 4 columns */}
                <div className="qr-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${actualCols}, ${stickerWidth}mm)`,
                    gridTemplateRows: `repeat(${actualRows}, ${stickerHeight}mm)`,
                    columnGap: `${columnGap}mm`,
                    rowGap: `${rowGap}mm`,
                    backgroundColor: 'white',
                    width: 'fit-content',
                    margin: '0 auto',
                    justifySelf: 'center'
                }}>
                    {qrCodes.map((qrCode) => (
                        <div key={qrCode.id} style={{
                            border: '1px solid #e0e0e0',
                            padding: '2mm',
                            margin: '0mm',
                            backgroundColor: '#fff',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: `${stickerWidth}mm`,
                            height: `${stickerHeight}mm`,
                            minWidth: `${stickerWidth}mm`,
                            minHeight: `${stickerHeight}mm`,
                            maxWidth: `${stickerWidth}mm`,
                            maxHeight: `${stickerHeight}mm`,
                            overflow: 'hidden',
                            boxSizing: 'border-box'
                        }}>
                            {/* Name section on the left */}
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                paddingRight: '2mm',
                                height: '100%'
                            }}>
                                <div style={{
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    textTransform: 'capitalize',
                                    lineHeight: '1.2',
                                    wordWrap: 'break-word',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%'
                                }}>
                                    {productName.length > 16 ? productName.substring(0, 16) + '...' : productName}
                                </div>
                            </div>

                            {/* QR Code section on the right */}
                            <div style={{
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <img
                                    src={qrCode.url}
                                    alt={`QR Code ${qrCode.id}`}
                                    style={{
                                        width: `${qrCodeSize}mm`,
                                        height: `${qrCodeSize}mm`,
                                        objectFit: 'contain',
                                        maxWidth: `${qrCodeSize}mm`,
                                        maxHeight: `${qrCodeSize}mm`
                                    }}
                                    onError={(e) => handleImageError(e, qrCode)}
                                />
                                <div style={{
                                    display: 'none',
                                    width: `${qrCodeSize}mm`,
                                    height: `${qrCodeSize}mm`,
                                    backgroundColor: '#f8f8f8',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '6px',
                                    color: '#999',
                                    textAlign: 'center'
                                }}>
                                    Error
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Debug info - only visible on screen */}
                <div style={{
                    position: 'absolute',
                    bottom: '5mm',
                    left: '5mm',
                    fontSize: '8px',
                    color: '#666',
                    display: 'none'
                }}>
                    Grid: {actualRows} rows × {actualCols} columns = {qrCodes.length} QR codes
                    <br />
                    Sticker: {stickerWidth}mm × {stickerHeight}mm | QR: {qrCodeSize}mm × {qrCodeSize}mm
                </div>
            </div>
        </>
    )
}

export default QRCodeGeneration